import React from "react";
import PropTypes from "prop-types";
import Rnd from "react-rnd";
// Material imports
import withStyles from "@mui/styles/withStyles";
import Dehaze from "@mui/icons-material/Dehaze";
import SendIcon from "@mui/icons-material/SendRounded";
//Limitless imports
import { withi18n } from "../../../../customer/components";
import { log as pilog } from "@limitless/common/utils";
import { isFieldEmpty } from "@limitless/common/utils";
import { useExpertMessageResponseContext, useExpertMessageContext } from "../../../../expert";
import { useExpertAIReviewContext } from "../../../../expert/hooks/expert-ai-review/ExpertAIReviewManagerContext";
import HeaderIconButton from "../../buttons/header-icon-button/HeaderIconButton";
import MessageEditorActions from "../message-editor-actions/MessageEditorActions";
import MessageReplyPreviewDialog from "../../dialogs/message-reply-preview-dialog/MessageReplyPreviewDialog";
import MessageConversationCompleteDialog from "../../dialogs/message-conversation-complete-dialog/MessageConversationCompleteDialog";
import MessageDuplicateDialogueDialog from "../../dialogs/message-duplicate-dialogue-dialog/MessageDuplicateDialogueDialog";
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import ImageAttachment from "@limitless/icons/ImageAttachment";
import MessageAttachmentDialog from "../../dialogs/message-attachment-dialog/MessageAttachmentDialog";
const DIALOGUE_TYPES = [{
  label: "Expert",
  value: "expert"
}, {
  label: "Customer",
  value: "customer"
}];
const MessageEditor = ({
  t,
  classes,
  message,
  onResize,
  renderFooterItems,
  onSendAttachment,
  onSendCustomerAttachment,
  onSendCustomerFileAttachment,
  onAddDialogue
}) => {
  var _aiReviewData$respond2;
  const [showPreviewDialog, setShowPreviewDialog] = React.useState(false);
  const [showDuplicateDialogueWarning, setShowDuplicateDialogueWarning] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(message.conversationState.editorState.previewResponse);
  const [showConversationComplete, setShowConversationComplete] = React.useState(false);
  const [rndHeight, setRndHeight] = React.useState(100);
  const [rndHeightPosition, setRndHeightPosition] = React.useState(0);
  const [showAttachment, setShowAttachment] = React.useState(false);
  const [attachmentDialogueType, setAttachmentDialogueType] = React.useState(DIALOGUE_TYPES[0].value);
  const {
    messageResponse,
    updateMessageResponse
  } = useExpertMessageResponseContext();
  const {
    dialogueError,
    isDupeDialogue
  } = useExpertMessageContext();
  const [initialAiResponse, setInitialAiResponse] = React.useState(null);
  const [previewReasonModifiedAIResponse, setPreviewReasonModifiedAIResponse] = React.useState(false);
  const [aiFeedbackText, setAiFeedbackText] = React.useState("");
  const {
    handleAIDraftSelected,
    isAIDraftSelected,
    aiReviewData
  } = useExpertAIReviewContext();
  const [isXssDetected, setIsXssDetected] = React.useState(false);
  const textArea = /*#__PURE__*/React.createRef();
  const rnd = /*#__PURE__*/React.createRef();
  const windowHeight = React.useRef(window.innerHeight);
  React.useEffect(() => {
    var _aiReviewData$respond;
    const nextDialogue = aiReviewData === null || aiReviewData === void 0 ? void 0 : (_aiReviewData$respond = aiReviewData.respondNextDialogue) === null || _aiReviewData$respond === void 0 ? void 0 : _aiReviewData$respond.nextDialogue;
    if (nextDialogue) {
      updateMessageResponse(nextDialogue);
      setInitialAiResponse(nextDialogue);
    }
  }, [aiReviewData === null || aiReviewData === void 0 ? void 0 : (_aiReviewData$respond2 = aiReviewData.respondNextDialogue) === null || _aiReviewData$respond2 === void 0 ? void 0 : _aiReviewData$respond2.nextDialogue]);
  const {
    editorState,
    hideEditor,
    allowAttachments
  } = message.conversationState;
  const dialogs = [];
  const log = pilog.getLogger("MessageEditor");
  //just making sure this doesn't re-render unnecessarily.
  log.debug("render");
  const currentWindowHeight = React.useMemo(() => {
    return (windowHeight === null || windowHeight === void 0 ? void 0 : windowHeight.current) * 2 / 5;
  }, [windowHeight]);
  const handleToggleAttachmentDialog = show => () => {
    setShowAttachment(show);
  };
  const handleAddCustomerAttachment = (url, caption) => {
    setShowAttachment(false);
    onSendCustomerAttachment(url, caption);
  };
  const handleCustomerFileUpload = (file, caption) => {
    setShowAttachment(false);
    onSendCustomerFileAttachment(file, caption);
  };
  const handleAddAttachment = (handleAttachFiles, caption) => {
    setShowAttachment(false);
    onSendAttachment(handleAttachFiles, caption);
  };
  const handleToggleAttachmentOptionDialog = show => () => {
    setShowAttachment(show);
  };
  if (showAttachment) {
    if (attachmentDialogueType === "expert") {
      dialogs.push(/*#__PURE__*/React.createElement(MessageAttachmentDialog, {
        key: 4,
        message: message,
        onCancelClick: handleToggleAttachmentDialog(false),
        onAttachClick: handleAddAttachment,
        dialogueTypes: DIALOGUE_TYPES,
        onOptionChange: setAttachmentDialogueType,
        attachmentDialogueType: attachmentDialogueType
      }));
    } else {
      dialogs.push(/*#__PURE__*/React.createElement(MessageAttachmentDialog, {
        key: 5,
        message: message,
        onCancelClick: handleToggleAttachmentDialog(false),
        onUrlAttachmentConfirmClick: handleAddCustomerAttachment,
        onAttachClick: handleCustomerFileUpload,
        includeFileUrl: true,
        dialogueTypes: DIALOGUE_TYPES,
        onOptionChange: setAttachmentDialogueType,
        attachmentDialogueType: attachmentDialogueType
      }));
    }
  }
  const handleSendClick = () => {
    if (isDupeDialogue(messageResponse)) {
      setShowDuplicateDialogueWarning(true);
    } else if (messageResponse !== initialAiResponse && isAIDraftSelected && initialAiResponse) {
      // must show preview if the message has changed from the AI-suggested one
      setShowPreviewDialog(true);
      setPreviewReasonModifiedAIResponse(true);
    } else {
      handlePreviewAndSend();
    }
  };
  const handlePreviewAndSend = () => {
    if (showPreview) {
      setShowPreviewDialog(true);
    } else {
      handleAddDialogue();
    }
  };
  const handleAddDialogue = () => {
    handleCancelPreview();
    if (isAIDraftSelected) {
      onAddDialogue(messageResponse, {
        isAIDraftSelected: true,
        feedback: aiFeedbackText,
        lastCustomerDialogueId: aiReviewData === null || aiReviewData === void 0 ? void 0 : aiReviewData.lastCustomerDialogueId
      });
    } else {
      onAddDialogue(messageResponse);
    }
    setRndHeight(100);
    setRndHeightPosition(0);
    handleAIDraftSelected(false);
  };
  const handleTextChange = event => {
    const value = event.currentTarget.value;
    const xssRegex = /<script[\s\S]*?>[\s\S]*?<\/script>|javascript:|<[^>]+on\w+\s*=/gi;
    setIsXssDetected(xssRegex.test(value));
    const height = event.target.scrollHeight + 10;
    const rows = event.target.rows;
    const rowHeight = 15;
    const totalrows = Math.ceil(height / rowHeight) - 1;
    textArea.current.style.height = 15 * (totalrows - rows);
    if (height > 100 && height <= currentWindowHeight) {
      const row = totalrows > 13 ? totalrows - rows : totalrows;
      setRndHeight(15 * row);
      const actualHeight = 15 * row;
      if (actualHeight >= 330) {
        setRndHeightPosition(`-${height - 45}`);
      } else if (actualHeight >= 240) {
        setRndHeightPosition(`-${height - 80}`);
      } else if (actualHeight > 99 && actualHeight < 240) {
        setRndHeightPosition(`-${height - 100}`);
      } else {
        setRndHeightPosition(0);
      }
    } else if (height > currentWindowHeight) {
      setRndHeight(currentWindowHeight);
      setRndHeightPosition(-(currentWindowHeight - 30));
    }
    updateMessageResponse(event.currentTarget.value);
  };
  const handleKeyPress = event => {
    if (event.key === "Enter" && event.shiftKey) {
      handleSendClick();
      event.preventDefault();
    }
  };
  const handleCancelPreview = () => {
    setShowPreviewDialog(false);
    setPreviewReasonModifiedAIResponse(false);
  };
  const handleSendDupeDialogue = () => {
    handleCancelDupeDialogue();
    handlePreviewAndSend();
  };
  const handleCancelDupeDialogue = () => {
    setShowDuplicateDialogueWarning(false);
  };
  if (hideEditor) {
    return /*#__PURE__*/React.createElement("div", null);
  }
  const isResponseEmpty = isFieldEmpty(messageResponse);
  const disableSendButton = isResponseEmpty || dialogueError || isXssDetected ? true : false;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.messageEditorContainer
  }, /*#__PURE__*/React.createElement(Rnd, {
    default: {
      x: 0,
      y: 0,
      width: "100%",
      height: "100%"
    },
    size: {
      width: "90%",
      height: `${rndHeight}%`
    },
    position: {
      x: 0,
      y: rndHeightPosition >= 0 ? 0 : parseInt(rndHeightPosition)
    },
    ref: rnd,
    className: classes.rndStyle,
    enableResizing: {
      top: true
    },
    resizeHandleClasses: {
      top: classes.draghandle
    },
    minHeight: 130,
    maxHeight: currentWindowHeight + 100,
    onResize: (e, direction, ref, delta, position) => {
      setRndHeightPosition(position.y);
      setRndHeight(ref.style.height);
    },
    disableDragging: true
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.wrapForm
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.subWrapForm
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.dragableBar
  }, /*#__PURE__*/React.createElement(Dehaze, {
    className: classes.iconColor
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.replyTextStyle
  }, /*#__PURE__*/React.createElement("textarea", {
    id: "replyTextArea",
    autoFocus: true,
    onChange: event => handleTextChange(event),
    value: messageResponse,
    type: "text",
    ref: textArea,
    placeholder: t(editorState.placeholder),
    onKeyPress: disableSendButton ? null : handleKeyPress,
    maxLength: "10000",
    spellCheck: "true",
    className: classes.textAreaStyle
  }), allowAttachments && /*#__PURE__*/React.createElement(IconButton, {
    icon: /*#__PURE__*/React.createElement(ImageAttachment, null),
    label: "Attach a file",
    tooltip: "Attach a file",
    tooltipPlacememnt: "top",
    id: "attachmentsButton",
    className: classes.icon,
    onClick: handleToggleAttachmentOptionDialog(true)
  }), /*#__PURE__*/React.createElement(MessageEditorActions, {
    t: t,
    message: message,
    classes: classes
  }), renderFooterItems && renderFooterItems())))), /*#__PURE__*/React.createElement("div", {
    className: classes.buttonContainer
  }, /*#__PURE__*/React.createElement(HeaderIconButton, {
    disabled: isXssDetected,
    onButtonClick: !disableSendButton && handleSendClick,
    tooltip: isXssDetected ? "Script tags or malicious content detected. Please remove to proceed." : dialogueError ? "A previous reply failed, try again first" : "Send",
    buttonId: "addDialogue",
    icon: /*#__PURE__*/React.createElement(SendIcon, {
      sx: {
        color: theme => theme.palette.primary.contrastText
      }
    }),
    buttonClass: classes.sendButton,
    color: "secondary"
  })), showConversationComplete && /*#__PURE__*/React.createElement(MessageConversationCompleteDialog, {
    onCancel: () => setShowConversationComplete(false)
  }), showPreviewDialog && /*#__PURE__*/React.createElement(MessageReplyPreviewDialog, {
    onShowPreviewChange: setShowPreview,
    showPreview: showPreview,
    onSendClick: handleAddDialogue,
    onCancelClick: handleCancelPreview,
    showAIFeedback: previewReasonModifiedAIResponse,
    setAiFeedbackText: setAiFeedbackText,
    aiFeedbackText: aiFeedbackText,
    isAIDraftSelected: isAIDraftSelected,
    t: t
  }), showDuplicateDialogueWarning && /*#__PURE__*/React.createElement(MessageDuplicateDialogueDialog, {
    onSendClick: handleSendDupeDialogue,
    onCancelClick: handleCancelDupeDialogue
  }), dialogs);
};
const styles = theme => {
  var _theme$palette$button, _theme$palette$button2;
  return {
    rndStyle: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      position: "inherit!important"
    },
    messageEditorContainer: {
      position: "relative",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-evenly",
      width: "100%",
      bottom: 0,
      height: 130
    },
    dragbarContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "top",
      background: "#ffffff"
    },
    dragableBar: {
      width: "100%",
      height: "20px",
      textAlign: "center",
      backgroundColor: theme.palette.info.main
    },
    attachButton: {
      color: theme.palette.buttons ? (_theme$palette$button = theme.palette.buttons) === null || _theme$palette$button === void 0 ? void 0 : (_theme$palette$button2 = _theme$palette$button.send) === null || _theme$palette$button2 === void 0 ? void 0 : _theme$palette$button2.color : theme.palette.primary.light
    },
    dialogRoot: {
      top: "calc(100vh - 222px)!important"
    },
    dialogRootForSingleItem: {
      top: "calc(100vh - 160px)!important"
    },
    draghandle: {
      height: "20px !important",
      top: "0 !important",
      width: "100% !important"
    },
    textarea: {
      resize: "both"
    },
    icon: {
      margin: "8px 8px 8px 0px"
    },
    replyTextStyle: {
      alignItems: "self-end",
      justifyContent: "space-evenly",
      width: "100%",
      padding: "12px 0px 8px 8px",
      display: "flex",
      outline: "none",
      height: "100%",
      background: theme.palette.background.elevation1
    },
    textAreaStyle: {
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.875rem",
      border: "0px none",
      overflow: "auto",
      resize: "none",
      cursor: "auto",
      marginRight: "8px",
      background: theme.palette.background.elevation1,
      color: theme.palette.grey[3],
      width: "calc(100% - 60px)",
      outline: "none",
      "&:hover": {
        outline: "none"
      },
      height: "100%"
    },
    iconColor: {
      color: theme.palette.primary.light
    },
    buttonContainer: {
      display: "flex",
      alignItems: "end"
    },
    leftButtonContainer: {
      display: "flex",
      flex: 1,
      justifyContent: "flex-start",
      alignItems: "center"
    },
    rightButtonContainer: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      width: "100%"
    },
    lightTooltip: {
      fontSize: 12
    },
    wrapForm: {
      display: "flex",
      justifyContent: "space-between",
      width: "97%",
      height: "100%",
      overflow: "hidden",
      position: "relative",
      alignItems: "flex-end",
      margin: `${theme.spacing(0)} 0px 4px auto`
    },
    subWrapForm: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      width: "100%",
      height: "100%",
      overflow: "hidden",
      position: "relative",
      borderRadius: "20px 20px 20px 20px",
      boxShadow: "0px 4px 6px -2px rgba(0, 0, 0, 0.15)"
    },
    sendButton: {
      background: theme.palette.primary.light,
      padding: 12,
      marginBottom: 5,
      marginLeft: 8,
      borderRadius: 16,
      "&:hover": {
        background: theme.palette.primary.light
      }
    },
    growWrap: {
      /* easy way to plop the elements on top of each other and have them both sized based on the tallest one's height */
      display: "grid"
    }
  };
};
export default withStyles(styles)(withi18n(MessageEditor));
MessageEditor.propTypes = {
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  message: PropTypes.object.isRequired,
  onAddDialogue: PropTypes.func.isRequired,
  isParentLoading: PropTypes.bool.isRequired,
  onSendAttachment: PropTypes.func.isRequired,
  onSendCustomerAttachment: PropTypes.func,
  onSendCustomerFileAttachment: PropTypes.func,
  onResize: PropTypes.func.isRequired,
  renderFooterItems: PropTypes.func,
  aiReviewData: PropTypes.object,
  isAIDraftSelected: PropTypes.bool,
  onAIDraftSelected: PropTypes.func
};